// src/lib/ordering-state.ts
import {
  isDeliveryOrderingEnabledForLocation,
  isMenuCartBrowsingEnabledForLocation,
} from "@/lib/features";
import type { PlatformLocation } from "@/lib/restaurant-platform";
import type { SectionServiceWindows } from "../../convex/lib/serviceWindows";

export type LiveOrderingFlags = {
  orderingEnabled: boolean;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
};

export type OrderingState = {
  /** Live query still resolving (consumers may keep optimistic defaults). */
  isLoading: boolean;
  /** Real online ordering is live: show Add-to-order, cart, checkout. */
  firstPartyOrderingEnabled: boolean;
  /** First-party tenant but ordering is not live: show browse + "call to order". */
  orderingPaused: boolean;
  /** Checkout is reachable (same condition as firstPartyOrderingEnabled). */
  checkoutEnabled: boolean;
  /**
   * Delivery is offered to guests. Requires ALL of: ordering live, the
   * tenant's static delivery capability, and the operator's runtime
   * isDeliveryEnabled switch. Unlike the pickup flags this defaults to FALSE
   * while the live query loads — a dark-launched option must never flash on
   * and then vanish.
   */
  deliveryOrderingEnabled: boolean;
  /** Per-location phone for the "call to order" fallback. */
  fallbackPhone: string;
};

/**
 * Combine the three gating layers into the storefront booleans:
 *   build (env) AND tenant capability (static) AND live operator flags (Convex).
 * While `live` is undefined (loading) the live flags default to enabled so the
 * common go-live-ON case renders without a flash.
 */
export function composeOrderingState(args: {
  buildEnabled: boolean;
  location: PlatformLocation;
  live: LiveOrderingFlags | undefined;
}): OrderingState {
  const { buildEnabled, location, live } = args;
  const tenantCapable = isMenuCartBrowsingEnabledForLocation(location);
  const liveOrdering = live?.orderingEnabled ?? true;
  const livePickup = live?.pickupEnabled ?? true;

  const firstPartyOrderingEnabled =
    buildEnabled && tenantCapable && liveOrdering && livePickup;
  // Fail dark: no optimistic default for delivery while loading.
  const liveDelivery = live?.deliveryEnabled ?? false;
  const deliveryOrderingEnabled =
    firstPartyOrderingEnabled &&
    isDeliveryOrderingEnabledForLocation(location) &&
    liveDelivery;
  const orderingPaused =
    location.ordering.mode === "first_party" && !firstPartyOrderingEnabled;

  return {
    isLoading: live === undefined,
    firstPartyOrderingEnabled,
    orderingPaused,
    checkoutEnabled: firstPartyOrderingEnabled,
    deliveryOrderingEnabled,
    fallbackPhone: location.contact.phone,
  };
}

export type PublicOrderingState = {
  orderingEnabled: boolean;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
  /**
   * Per-section service hours, undefined for any location that has not
   * configured them (unconfigured => always servable; see
   * convex/lib/serviceWindows.ts).
   */
  serviceWindows?: SectionServiceWindows;
};

type OrderType = "pickup" | "delivery";

export function shouldResetDeliveryOrderType(args: {
  isLoading: boolean;
  deliveryOrderingEnabled: boolean;
  orderType: OrderType;
}) {
  return (
    !args.isLoading &&
    !args.deliveryOrderingEnabled &&
    args.orderType === "delivery"
  );
}

export function resolveOrderTypeSelection(args: {
  requestedOrderType: OrderType;
  hasDeliveryAddress: boolean;
  hasDeliveryZone: boolean;
}): OrderType | "prompt_address" {
  if (args.requestedOrderType === "pickup") return "pickup";
  return args.hasDeliveryAddress && args.hasDeliveryZone
    ? "delivery"
    : "prompt_address";
}

export function getDeliveryAddressDraftError(args: {
  street: string;
  city: string;
  state: string;
  zip: string;
}) {
  if (!args.street.trim()) return "Enter a street address.";
  if (!args.city.trim()) return "Enter a city.";

  const state = args.state.trim();
  const zip = args.zip.replace(/\D/g, "");
  if (state.length !== 2 || zip.length !== 5) {
    return "Enter a two-letter state and five-digit ZIP code.";
  }

  return null;
}

/** Project the customer-facing subset of order settings. */
export function publicOrderingStateFromSettings(settings: {
  isOrderingEnabled: boolean;
  isPickupEnabled: boolean;
  isDeliveryEnabled: boolean;
  serviceWindows?: SectionServiceWindows;
}): PublicOrderingState {
  return {
    orderingEnabled: settings.isOrderingEnabled,
    pickupEnabled: settings.isPickupEnabled,
    deliveryEnabled: settings.isDeliveryEnabled,
    serviceWindows: settings.serviceWindows,
  };
}
